<template>
  <div
    v-click-outside="onClickOutside"
    class="fact-finder-slider-facet"
  >
    <slider-element
      v-for="element in elements"
      :key="element.name"
      ref="sliderElement"
      :element="element"
      :group="group"
      @change="onChange"
    />
  </div>
</template>

<script setup lang="ts">
// @ts-expect-error - click-outside-vue3 ships no type declarations
import clickOutside from 'click-outside-vue3';
import { throttle } from 'throttle-debounce';
import { ref, useTemplateRef } from 'vue';

import SliderElement from './SliderElement.vue';

const vClickOutside = clickOutside.directive;

type ThrottledFn = (() => void) & { cancel: () => void };

defineOptions({ name: 'FactFinderSliderFacet' });

const props = withDefaults(
  defineProps<{
    group?: Record<string, any>;
  }>(),
  {
    group: () => ({}),
  },
);

const emit = defineEmits<{
  (e: 'change', ...facets: any[]): void;
}>();

// NOTE: elements will not be reactive, since it changes when a value updates, thus
// re-creating the slider and resetting its values
const elements = ref<any[]>(props.group.elements);

const sliderElement = useTemplateRef<InstanceType<typeof SliderElement>[]>('sliderElement');

let changeThrottle: ThrottledFn | undefined;

function onApply() {
  const facets = (sliderElement.value ?? [])
    .map((slider) => {
      const {
        element: { name, absoluteMinValue, absoluteMaxValue, unit },
        lower,
        upper,
      } = slider;
      const { associatedFieldName } = props.group;

      const newLower = String(lower);
      const newUpper = String(upper);

      if (Number(lower) <= Number(absoluteMinValue) && Number(upper) >= Number(absoluteMaxValue)) {
        return {
          value: false,
          element: {
            name,
            associatedFieldName,
            selected: false,
            absoluteMinValue,
            absoluteMaxValue,
            unit,
          },
        };
      }

      return {
        value: true,
        element: {
          name,
          associatedFieldName,
          selected: true,
          selectedMinValue: newLower,
          selectedMaxValue: newUpper,
        },
      };
    })
    .filter((facet) => facet !== null);
  if (facets.length) {
    emit('change', ...facets);
  }
}

function onClickOutside() {
  changeThrottle?.cancel();
  onApply();
}

function onChange() {
  if (!changeThrottle) {
    changeThrottle = throttle(400, async () => {
      onApply();
    }) as ThrottledFn;
  }

  changeThrottle();
}
</script>

<style scoped src="./fact-finder-slider-facet" />
